/*
  shintop-contact-keep — keeps a failed enquiry alive across Shopify's POST round trip.

  WHY. Naoya, 2026-09-22: "when it sent successfully, it shows like this, but when it is
  errored, it shows nothing and only email field is kept and other field would be empty."

  Both halves of that are real, and the second one has a cause worth writing down, because it
  looks like a theme bug and is actually a Shopify contract.

  Shopify's contact form exposes exactly four values back to Liquid after a failed submit:
  `form.name`, `form.email`, `form.phone` and `form.body`. They are filled from the CANONICAL
  parameter names — `contact[name]`, `contact[email]`, `contact[phone]`, `contact[body]`. Every
  other `contact[...]` field is passed to the notification email and then forgotten.

  This form names its fields after their visible labels, deliberately: the field name is what
  Naoya reads in the enquiry email, which is why the message says "Your message" and not Dawn's
  "Comment". So the names are `contact[Your message]`, `contact[What is it about]` and
  `contact[Phone number]` — and NONE of those is a canonical key. `form.body` and `form.phone`
  are therefore nil on every error, and the topic never had a server-side home at all. Only
  `contact[email]` happens to be spelled canonically, which is why the email was the one field
  that survived. The name posts as `contact[Name]` — canonical but for a capital N — and whether
  Shopify matches that case-insensitively is unmeasured (B242), so this file restores it too.

  ⚠️ THIS IS TRUE OF DAWN TOO. `templates.contact.form.phone` is "Phone number" and
  `.comment` is "Comment" in `locales/en.default.json`, so stock Dawn's `value="{{ form.phone }}"`
  and its textarea's `{{ form.body }}` are dead in an English store as well. Copying Dawn harder
  would not have fixed this. The only server-side cure is renaming the fields to the canonical
  keys, which would change what lands in his inbox — his call, not a round's.

  WHAT IT DOES. Stashes the form's own values on submit, and if the very next load of the same
  page is not a success, puts them back and says the message was not sent.

  FOUR THINGS THAT MAKE IT SAFE

  1. IT DEGRADES TO NOTHING. No stash, no sessionStorage (private mode throws on write), or this
     file 404s — and the form is exactly the form that shipped before it. It adds behaviour to a
     failure path; it never stands between a visitor and the Send button.

  2. IT ONLY EVER SPEAKS WHEN THE SERVER DID NOT. If Shopify rendered its own error block, that
     block is the message and this file restores the fields silently. The notice is for the case
     Naoya actually hit: a round trip that came back with no success and no error.

  3. IT CANNOT CLAIM A FAILURE THAT DID NOT HAPPEN. The stash is written in the submit handler
     and read on the next load of the same path, so "not sent" is inferred from a submit this
     page made, never from an empty form or a plain reload. A success clears it before anything
     is read.

  4. IT DOES NOT HOARD. The stash holds a name, an email, a phone number and a message — so it
     is cleared the moment it is used or the moment a send succeeds, it is sessionStorage (one
     tab, gone when the tab closes) and never localStorage, and it expires on its own.

  Strings come from the locale through data attributes — never from this file.
*/

const KEEP_TTL = 30 * 60 * 1000; // a stash older than this is stale, not a failed submit

class ShintopContactKeep extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    if (!this.form) return;

    this.key = `shintop:contact:${this.form.id || 'form'}:${window.location.pathname}`;

    this.form.addEventListener('submit', () => this.stash());
    this.restore();
  }

  /* sessionStorage throws on write in Safari private mode and can be disabled outright, so
     every access is guarded. A storage failure must cost the visitor nothing. */
  read() {
    try {
      const raw = window.sessionStorage.getItem(this.key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  clear() {
    try {
      window.sessionStorage.removeItem(this.key);
    } catch (e) {
      /* nothing to do — the stash simply expires instead */
    }
  }

  /* Only the enquiry's own fields. `form_type` and `utf8` are Shopify's hidden plumbing and are
     re-rendered correctly by the server every time; re-seeding them from a stash could only ever
     make them wrong. */
  fields() {
    return Array.from(this.form.elements).filter(
      (el) => el.name && el.name.indexOf('contact[') === 0 && el.type !== 'hidden'
    );
  }

  stash() {
    const values = {};
    this.fields().forEach((el) => {
      /* A RADIO ALWAYS HAS A VALUE — round 25, 2026-09-24, when the topic stopped being a
         <select> and became a row of chips. Every radio in a group reports its OWN value whether
         or not it is the one chosen, so the plain `el.value` below would have stashed the last
         chip in the row every time and restored a topic the visitor never picked. Only the
         checked one is an answer; an unchecked group is the same "said nothing" the empty first
         <option> used to mean. */
      if (el.type === 'radio') {
        if (el.checked) values[el.name] = el.value;
        return;
      }
      if (el.value) values[el.name] = el.value;
    });
    if (!Object.keys(values).length) return;
    try {
      window.sessionStorage.setItem(this.key, JSON.stringify({ t: Date.now(), values }));
    } catch (e) {
      /* over quota or storage disabled — the form behaves as it did before this file */
    }
  }

  restore() {
    /* A success is the end of the story: clear first, so a later reload of this page can never
       resurrect a message that was actually sent. */
    if (this.form.querySelector('[data-shintop-contact-ok]')) {
      this.clear();
      return;
    }

    const stash = this.read();
    if (!stash || !stash.values || Date.now() - stash.t > KEEP_TTL) {
      if (stash) this.clear();
      return;
    }
    this.clear();

    let restored = 0;
    let insideDisclosure = false;
    const disclosure = this.form.querySelector('details');

    this.fields().forEach((el) => {
      const saved = stash.values[el.name];
      if (saved === undefined) return;
      /* Radios are restored by CHECKING the right one, not by writing `.value` — writing it would
         change what that radio submits and leave the group unchosen, which looks identical to a
         lost answer. A chip whose value no longer matches any topic (the setting was edited
         between the failed submit and the reload) simply stays unchecked. */
      if (el.type === 'radio') {
        if (el.value !== saved || el.checked) return;
        if (this.form.querySelector('input[name="' + CSS.escape(el.name) + '"]:checked')) return;
        el.checked = true;
        restored += 1;
        if (disclosure && disclosure.contains(el)) insideDisclosure = true;
        return;
      }
      if (el.value) return;
      el.value = saved;
      /* A <select> silently ignores a value it has no option for, so only count what took. */
      if (el.value !== saved) return;
      restored += 1;
      if (disclosure && disclosure.contains(el)) insideDisclosure = true;
    });

    if (!restored) return;

    /* A restored topic or phone number behind a closed disclosure is indistinguishable from a
       lost one — the whole point of putting it back is that the visitor can see it. */
    if (insideDisclosure) disclosure.open = true;

    /* The server said nothing at all, and the visitor is looking at a form that appears to have
       done nothing. That is the case this element was built for. */
    if (!this.form.querySelector('[data-shintop-contact-error]')) this.announce();
  }

  announce() {
    const message = this.dataset.notSent;
    if (!message) return;

    const box = document.createElement('div');
    box.className = 'shintop-contact__error';
    box.setAttribute('role', 'alert');
    box.setAttribute('tabindex', '-1');
    box.setAttribute('data-shintop-contact-error', '');
    const p = document.createElement('p');
    p.textContent = message;
    box.appendChild(p);

    this.form.insertBefore(box, this.form.firstElementChild);
    box.focus();
  }
}

if (!customElements.get('shintop-contact-keep')) {
  customElements.define('shintop-contact-keep', ShintopContactKeep);
}
